import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../utils/api-client';
import { climbPhotoPaint, useClimbPhotoEffect, type ClimbPhotoEffect } from '../climb-photo-effect';
import { holdsForMode, useHoldsDisplayMode } from '../holds-display-mode';
import { hasCorridor, isDrawableLine } from '../route-line';
import { routeLineDisplay, useRouteLineDisplayMode } from '../route-line-display-mode';
import { boardWallHolds, climbWall } from '../board-grid/climb-wall';
import type { BoardClimb, Climb } from '../../../types/climb';
import type { Board } from '../../../types/device';
import type { ThumbnailInfo, ThumbnailPicture } from './climb-thumbnail';
import { WATERMARK_LOGO_URL } from './watermark';

/** What the card says under the photo. */
export function thumbnailInfo(climb: Climb): ThumbnailInfo {
  return {
    grade: climb.grade_visible === false ? null : climb.difficulty || null,
    holdType: climb.hold_color || null,
    holdColors: climb.hold_color_hex ?? [],
    rating: climb.average_rating ?? null,
  };
}

/**
 * The climb's photo with the reader's photo effect painted on: the picture the
 * API draws for a shared link's preview (`/api/climbs/:id/og_image`), the same
 * numbers done once on the server rather than again on every phone — whose
 * canvas, on Safari before 18, has no `filter` to do them with. Null where the
 * photo is shown as it was taken.
 */
export function climbEffectImageUrl(climb: Climb, effect: ClimbPhotoEffect): string | null {
  if (!climb.from_scan || !climbPhotoPaint(effect).holds) return null;

  const params = new URLSearchParams({ photo_effect: effect, v: climb.updated_at });
  return `${apiClient.getBaseURL()}/api/climbs/${encodeURIComponent(climb.slug)}/og_image?${params}`;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Drawn onto a canvas that is then exported, which a picture from another
    // origin would taint without it.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${url}`));
    img.src = url;
  });
}

export type ClimbThumbnailStatus = 'loading' | 'ready' | 'none';

/**
 * The picture a climb's card is drawn from, as the reader is looking at the
 * climb: with their photo effect, the holds their holds toggle shows (or the
 * line their line toggle shows), and on a board problem the board's photo with
 * the problem's holds on it. `none` for a climb with no picture to draw.
 *
 * `board` is the board a board problem is viewed through, where the page has
 * it; without it the problem's own copy of its wall is used.
 */
export function useClimbThumbnail(climb: Climb, board?: Board | null) {
  const effect = useClimbPhotoEffect(climb);
  const { mode: holdsMode } = useHoldsDisplayMode();
  const { mode: lineMode } = useRouteLineDisplayMode();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [status, setStatus] = useState<ClimbThumbnailStatus>('loading');

  const wall = useMemo(
    () => (climb.type === 'BoardClimb' ? climbWall(climb as BoardClimb, board) : null),
    [climb, board],
  );
  const plainUrl = wall ? wall.imageUrl : climb.image_url ?? climb.image_medium_url ?? climb.image_small_url;
  const effectUrl = wall ? null : climbEffectImageUrl(climb, effect);

  useEffect(() => {
    let active = true;
    setImage(null);
    if (!plainUrl) {
      setStatus('none');
      return;
    }
    setStatus('loading');
    // The painted picture where there is one, and the plain photo if the API
    // cannot be reached for it: a card without the effect beats no card.
    const load = effectUrl ? loadImage(effectUrl).catch(() => loadImage(plainUrl)) : loadImage(plainUrl);
    load
      .then((img) => {
        if (!active) return;
        setImage(img);
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('none');
      });
    return () => { active = false; };
  }, [plainUrl, effectUrl]);

  const picture = useMemo((): ThumbnailPicture | null => {
    if (!image) return null;
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) return null;

    if (wall) {
      const holds = boardWallHolds(wall, width / height);
      return { image, width, height, holds, glowHolds: holds, line: null };
    }
    // A route drawn as a line answers to the line's own toggle, and any holds
    // the setter also circled come and go with it — as on the climb's page.
    const line = isDrawableLine(climb.line) ? climb.line : null;
    const showsLine = !!line && routeLineDisplay(lineMode, hasCorridor(line)).line;
    const mode = line ? (showsLine ? 'all' : 'none') : holdsMode;
    return {
      image,
      width,
      height,
      holds: holdsForMode(climb.holds ?? [], mode),
      glowHolds: climb.holds ?? [],
      line: showsLine ? line : null,
    };
  }, [image, wall, climb.line, climb.holds, lineMode, holdsMode]);

  const info = useMemo(() => thumbnailInfo(climb), [climb]);

  return { picture, info, status };
}

/** The logo the watermark is drawn with, once loaded. */
export function useWatermarkLogo(): HTMLImageElement | null {
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    let active = true;
    loadImage(WATERMARK_LOGO_URL)
      .then((img) => { if (active) setLogo(img); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return logo;
}
